<template>
    <div
        class="fixed top-16 hidden h-[calc(100vh-4rem)] shrink-0 flex-col overflow-y-hidden bg-white pt-5 md:flex transition-[width] duration-200"
        :class="{
            'z-30': opened,
            'border-r border-gray-200': !opened || compact,
            'w-16': !opened || compact,
            'w-[250px]': opened && !compact,
            'xl:w-[250px]': !compact,
        }"
    >
        <transition
            enter-active-class="transition duration-200 ease-out transform"
            enter-from-class="-translate-x-10 opacity-0 "
            enter-to-class="translate-x-0 opacity-100 "
            leave-active-class="transition duration-100 ease-in transform"
            leave-from-class="translate-x-0 opacity-100"
            leave-to-class="-translate-x-10 opacity-0"
        >
            <div
                v-if="opened"
                class="hidden xl:hidden"
                :class="{
                    'absolute left-[63px] top-0 z-[-2] h-full w-[161px] bg-white md:block':
                        opened,
                }"
            ></div>
        </transition>

        <div
            class="relative z-20 flex h-full min-h-0 flex-col"
            :class="[
                hasHubspotOverlay ? 'pt-[62px]' : 'pt-[24px]',
                isShortScreen
                    ? 'overflow-y-scroll'
                    : hasHubspotOverlay
                      ? 'overflow-y-auto'
                      : 'overflow-y-hidden',
            ]"
        >
            <nav
                class="flex-1 space-y-[5px] px-[10px] pb-[25px]"
                aria-label="Sidebar"
                role="navigation"
            >
                <div
                    v-if="!compact"
                    class="relative hidden h-10 md:block xl:hidden"
                >
                    <transition
                        enter-active-class="transition ease-in-out duration-800"
                        enter-from-class="transform scale-0 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-100 ease-in "
                        leave-from-class="opacity-100 "
                        leave-to-class="opacity-0 "
                    >
                        <button
                            v-if="!opened"
                            class="absolute mx-2 mb-[16px] h-6 w-6 text-gray-600 xl:hidden"
                            @click="$emit('open')"
                        >
                            <Bars3BottomLeftIcon />
                        </button>
                    </transition>

                    <transition
                        enter-active-class="transition duration-1000 ease-in-out"
                        enter-from-class="transform rotate-180 opacity-0 scale-80"
                        enter-to-class="transform scale-100 rotate-0 opacity-100"
                        leave-active-class="transition duration-75 ease-in "
                        leave-from-class="opacity-100 "
                        leave-to-class="opacity-0 "
                    >
                        <button
                            v-if="opened"
                            class="absolute mx-2 mb-[16px] h-6 w-6 text-gray-600 xl:hidden"
                            @click="$emit('close')"
                        >
                            <XMarkIcon />
                        </button>
                    </transition>
                </div>

                <template v-for="link in links" :key="link.id">
                    <SidebarLinkGroup
                        v-if="isLinkGroup(link)"
                        :group="link"
                        :opened="opened"
                        :compact="compact"
                        @navigate="$emit('close')"
                    />

                    <inertia-link
                        v-else
                        v-tooltip="{
                            content: link.tooltip ?? link.name,
                            delay: {
                                show: link.tooltip ? 300 : 1250,
                                hide: 150,
                            },
                        }"
                        :href="link.locked ? '#' : link.href"
                        :aria-current="isLinkActive(link) ? 'page' : null"
                        class="group block w-full"
                        @click="
                            link.locked
                                ? $event.preventDefault()
                                : $emit('close')
                        "
                    >
                        <div
                            class="flex h-12 w-full items-center rounded-[6.75px] px-4 py-3 transition-all duration-200"
                            :class="[
                                compact
                                    ? 'justify-center'
                                    : (opened ? 'gap-7' : 'justify-center') +
                                      ' xl:justify-start xl:gap-7',
                                isLinkActive(link)
                                    ? 'bg-gray-200 text-gray-600'
                                    : 'text-gray-500 hover:bg-gray-200 hover:text-gray-600',
                                link.locked ? 'opacity-60' : '',
                            ]"
                            :data-helper-tooltip="link.name.toLocaleLowerCase()"
                        >
                            <component
                                :is="link.icon"
                                class="h-5 w-5 shrink-0 transition duration-200 group-hover:text-gray-600"
                            />

                            <span
                                class="truncate text-base font-medium"
                                :class="
                                    compact
                                        ? 'hidden'
                                        : opened
                                          ? 'inline'
                                          : 'hidden xl:inline'
                                "
                            >
                                {{ link.name }}
                            </span>

                            <div
                                v-if="
                                    link.count !== undefined ||
                                    link.badge ||
                                    link.locked
                                "
                                class="ml-auto hidden items-center gap-2"
                                :class="
                                    compact
                                        ? 'hidden'
                                        : opened
                                          ? 'inline-flex'
                                          : 'hidden xl:inline-flex'
                                "
                            >
                                <LockClosedIcon
                                    v-if="link.locked"
                                    class="h-4 w-4 text-gray-400"
                                />
                                <span
                                    v-if="link.count !== undefined"
                                    class="h-5 min-w-[37px] items-center justify-center rounded-2xl bg-gray-100 px-2 text-xs font-normal text-gray-500"
                                >
                                    {{ link.count }}
                                </span>
                                <span
                                    v-if="link.badge"
                                    class="inline-flex h-5 items-center justify-center rounded-2xl bg-[#FF421E] px-2 text-[10px] font-semibold text-white"
                                >
                                    {{ link.badge }}
                                </span>
                            </div>
                        </div>
                    </inertia-link>
                </template>
            </nav>

            <div class="mt-auto pb-2">
                <FreePlanStatusCounter
                    v-if="!compact"
                    :opened="opened"
                    :has-free-plan="globalProps.hasFreePlan"
                    :free-plan-last-period-number-of-report="
                        globalProps.freePlanLastPeriodNumberOfReport
                    "
                    @upgrade="goToSubscriptions"
                />

                <FreeTrialStatusCounter
                    v-if="!compact"
                    :opened="opened"
                    :is-trialing="globalProps.isTrialing"
                    :is-trial-expired="globalProps.isTrialExpired"
                    :is-subscribed="globalProps.isSubscribed"
                    :has-free-plan="globalProps.hasFreePlan"
                    :total-days="globalProps.totalDays"
                    :default-trial-days="globalProps.defaultTrialDays"
                    :used-days="globalProps.usedDays"
                    :ai-access="globalProps.aiChat"
                    @upgrade="goToSubscriptions"
                />

                <div class="mt-[4px] w-full px-[10px] pb-[7px]">
                    <LanguageSwitcher
                        :compact="compact || (!opened && !isXl)"
                        :sidebar-right="
                            compact || (!opened && !isXl) ? 64 : 250
                        "
                    />
                </div>

                <!--TODO: delete sidepanel teams menu later fully as a whole implementation-->

                <div v-if="compact" class="w-full px-[10px] pb-[7px]">
                    <a
                        v-tooltip="{
                            content: 'Changelog',
                            delay: { show: 1250, hide: 150 },
                        }"
                        href="/changelog"
                        class="group flex h-12 w-full items-center justify-center rounded-[6.75px] px-4 py-3 text-gray-500 transition-all duration-200 hover:bg-gray-200 hover:text-gray-600"
                        @click="$emit('close')"
                    >
                        <LightBulbIcon
                            class="h-5 w-5 shrink-0 transition duration-200 group-hover:text-gray-600"
                        />
                    </a>
                </div>

                <div
                    v-if="!compact"
                    class="w-16 flex-1 space-y-1 px-3 transition-[width] duration-200 w-full xl:px-4"
                >
                    <a
                        v-tooltip="{
                            content: 'Changelog',
                            delay: { show: 1250, hide: 150 },
                        }"
                        href="/changelog"
                        class="group flex items-center justify-between rounded-md px-2 py-2 text-sm font-medium transition-all xl:w-auto"
                        @click="$emit('close')"
                    >
                        <div class="w-full">
                            <div
                                v-if="!opened"
                                class="flex h-10 items-center justify-center xl:hidden"
                            >
                                <LightBulbIcon
                                    class="h-6 w-6 shrink-0 text-gray-500 transition duration-200 group-hover:text-gray-600"
                                />
                            </div>

                            <div
                                v-if="globalProps.latestChangelog"
                                class="border-t border-gray-200 pt-4 space-y-1"
                                :class="opened ? 'block' : 'hidden xl:block'"
                            >
                                <p
                                    class="text-base font-semibold text-gray-600 whitespace-nowrap"
                                >
                                    DMARCeye {{ $t("vue.version") }}
                                    {{ globalProps.latestChangelog.version }}
                                </p>
                                <p
                                    class="text-sm text-gray-400 whitespace-nowrap"
                                >
                                    {{ $t("vue.last_update") }}
                                    {{
                                        globalProps.latestChangelog.release_date
                                    }}
                                </p>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {
    Bars3BottomLeftIcon,
    XMarkIcon,
    LightBulbIcon,
    LockClosedIcon,
} from "@heroicons/vue/24/outline";
import FreePlanStatusCounter from "@/components/Navigation/FreePlanStatusCounter.vue";
import FreeTrialStatusCounter from "@/components/Navigation/FreeTrialStatusCounter.vue";
import SidebarLinkGroup from "@/components/Navigation/SidebarLinkGroup.vue";
import { isLinkGroup } from "@/data/sidebar";

import { usePage } from "@inertiajs/vue3";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { router } from "@inertiajs/vue3";
import { useHubspotOverlay } from "@/composables/useHubspotOverlay.ts";

const isXl = ref(false);
const isShortScreen = ref(false);
const { hasHubspotOverlay } = useHubspotOverlay();

const updateIsXl = () => {
    isXl.value = window.matchMedia("(min-width: 1326px)").matches;
};

const updateIsShortScreen = () => {
    isShortScreen.value = window.innerHeight <= 800;
};

const updateScreenState = () => {
    updateIsXl();
    updateIsShortScreen();
};

onMounted(() => {
    updateScreenState();
    window.addEventListener("resize", updateScreenState, { passive: true });
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", updateScreenState);
});

const goToSubscriptions = () => {
    router.visit(route("billing.subscriptions.index"));
};

defineProps({
    opened: {
        type: Boolean,
        default: false,
    },
    compact: {
        type: Boolean,
        default: false,
    },
});

defineEmits(["open", "close"]);

const { links, isLinkActive } = sidebar();

const globalProps = computed(() => usePage<IRhizomPageProps>().props);
</script>

<style></style>
